import Cookies from "js-cookie";
import { waitForConsent, LibCategories } from "src/utils/consent";
import { trackTask } from "src/utils/monitoring/events/tasks";
import { registerHandler } from "../handler";
import { stripURLParams } from "src/utils/urlParams";
import {
  getNoomSessionStorage,
  setNoomSessionStorage,
} from "src/utils/noomSessionStorage";
import { SessionStorageKey } from "src/utils/constants";
import { getRouteId } from "src/utils/meristemContext";

// ---------------------------------------------------------------------------
// VWO Offline-conversion publisher (custom events)
// Docs: https://help.vwo.com/hc/en-us/articles/25754666953241-How-to-Track-Offline-Conversions-Using-VWO-Data360
// ---------------------------------------------------------------------------

const VWO_ACCOUNT_ID = 1069422;
const VWO_ENDPOINT = `https://dev.visualwebsiteoptimizer.com/events/t?a=${VWO_ACCOUNT_ID}`; // Change host for EU/IN DC

// Constants
const VWO_UUID_COOKIE = "_vwo_uuid";
const EVENT_NAMES = {
  BUYFLOW_VISIT: "buyflowVisit",
  LEAD: "lead",
  PURCHASE: "purchase",
  PURCHASE_INTENT: "purchaseIntent",
} as const;

const FETCH_OPTIONS: RequestInit = {
  method: "POST",
  mode: "no-cors",
  credentials: "omit",
  headers: { "Content-Type": "application/json" },
};

// Types
interface VWOEventProps {
  [key: string]: unknown;
  page?: { url: string };
  route?: string;
  totalAmount?: number;
  productId?: string;
  planType?: string;
  isCustomEvent?: boolean;
  vwoMeta?: { source: string };
}

interface VWOPayload {
  d: {
    msgId: string;
    visId: string;
    event: {
      name: string;
      time: number;
      props: VWOEventProps;
    };
    sessionId: number;
  };
}

/**
 * Gets the VWO UUID from cookies
 */
const getVwoUuid = (): string | undefined => Cookies.get(VWO_UUID_COOKIE);

/**
 * Gets current page information for VWO tracking
 */
function getPageInfo() {
  if (typeof window === "undefined") return undefined;
  return {
    url: stripURLParams(window.location.href),
  };
}

/**
 * Builds VWO event payload
 */
function buildPayload(
  visId: string,
  eventName: string,
  props?: Record<string, unknown>
): VWOPayload {
  const msNow = Date.now();
  const page = getPageInfo();

  return {
    d: {
      msgId: `${visId}-${msNow}`,
      visId,
      event: {
        name: eventName,
        time: msNow,
        props: {
          isCustomEvent: true,
          vwoMeta: { source: "client" },
          ...(page ? { page } : {}),
          ...(props || {}),
        },
      },
      sessionId: Math.floor(msNow / 1000),
    },
  };
}

function fireEvent(
  eventName: string,
  props?: Record<string, unknown>
): Promise<Response | void> {
  const visId = getVwoUuid();

  if (!visId) {
    return Promise.resolve();
  }

  const endpoint = `${VWO_ENDPOINT}&en=${eventName}`;
  const payload = buildPayload(visId, eventName, props);

  return fetch(endpoint, { ...FETCH_OPTIONS, body: JSON.stringify(payload) });
}

/**
 * Registers all VWO event handlers
 */
export default function registerHandlers() {
  // Init – fire once per session to record visit start
  registerHandler("init", () => {
    waitForConsent([LibCategories.targetingCookies]).then(() => {
      const alreadyTracked = getNoomSessionStorage(
        SessionStorageKey.trackedVwoVisit
      );

      if (!alreadyTracked) {
        const page = getPageInfo();
        const route = getRouteId();

        setNoomSessionStorage(SessionStorageKey.trackedVwoVisit, true);

        const task = trackTask("vwoInitEvent");
        task.wrap(() =>
          fireEvent(EVENT_NAMES.BUYFLOW_VISIT, {
            route,
            currentUrl: page?.url,
          })
        );
      }
    });
  });

  // Lead – with route information
  registerHandler("lead", () => {
    waitForConsent([LibCategories.targetingCookies]).then(() => {
      const route = getRouteId();
      const task = trackTask("vwoLeadEvent");

      task.wrap(() => fireEvent(EVENT_NAMES.LEAD, { route }));
    });
  });

  // Purchase – with order details and product information
  registerHandler("purchase", ({ order, plan }) => {
    waitForConsent([LibCategories.targetingCookies]).then(() => {
      const task = trackTask("vwoPurchaseEvent");
      task.wrap(() =>
        fireEvent(EVENT_NAMES.PURCHASE, {
          totalAmount: order.eltv_13_months ?? 0,
          productId: plan.braintree_id,
        })
      );
    });
  });

  // PurchaseIntent – with estimated value and plan type
  registerHandler("purchaseIntent", ({ estimatedValue, medPlanType }) => {
    waitForConsent([LibCategories.targetingCookies]).then(() => {
      const task = trackTask("vwoPurchaseIntentEvent");
      task.wrap(() =>
        fireEvent(EVENT_NAMES.PURCHASE_INTENT, {
          totalAmount: estimatedValue ?? 0,
          planType: medPlanType,
        })
      );
    });
  });
}
